import classNames from 'classnames'
import React from 'react'

import type {
  IPulseHighlight,
  PulseHighlightProps,
  PulseIntent,
} from 'shared-libs/components/ui2/behavior/pulse-highlight'

import './_pulse-highlight.scss'

// Web twin of NM's UI2PulseHighlight. Wraps its children and renders a
// pointer-events-none outline overlay that briefly fades in and out when
// `pulse()` is called on the ref. Callers hold a typed IPulseHighlight ref.
//
// The ring color follows the pulse's intent — 'error' is red, 'focus'
// keeps the primary highlight — unless an explicit `color` overrides it.
export interface IUI2PulseHighlightRef extends IPulseHighlight {}

interface IWebProps extends PulseHighlightProps {
  className?: string
}

export const UI2PulseHighlight = React.forwardRef<IUI2PulseHighlightRef, IWebProps>(
  ({ children, style, borderRadius = 4, color, className }, ref) => {
    const overlayRef = React.useRef<HTMLDivElement | null>(null)
    // Bump a nonce so the same-named CSS animation restarts on every trigger.
    const [nonce, setNonce] = React.useState(0)
    const [intent, setIntent] = React.useState<PulseIntent>('focus')

    React.useImperativeHandle(ref, () => ({
      pulse: (nextIntent: PulseIntent = 'focus') => {
        setIntent(nextIntent)
        setNonce((n) => n + 1)
      },
    }))

    return (
      <div
        className={classNames('ui2-pulseHighlight', className)}
        style={style as React.CSSProperties | undefined}
      >
        {children}
        <div
          key={nonce}
          ref={overlayRef}
          className={classNames(
            'ui2-pulseHighlight-outline',
            `ui2-pulseHighlight-outline--${intent}`,
            nonce > 0 && 'ui2-pulseHighlight-outline--active',
          )}
          style={{ borderRadius, borderColor: color }}
        />
      </div>
    )
  },
)
UI2PulseHighlight.displayName = 'UI2PulseHighlight'
